import AurButton from '../../common/aur-button/AurButton';
import {Box, Typography} from '@mui/material';
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import AurSvgIcon from '../../common/aur-svg-icon/AurSvgIcon';
import {RootState} from '../../../redux/store';
import {useDispatch, useSelector} from 'react-redux';
import IndexingDetails from './IndexingDetails';
import {
  IIndexingDetails,
  setIndexingData,
  setSelectedIndex,
} from '../../../redux/slices/indexingSlice';
import {ReactComponent as GlowRight} from '../../../assests/svg/glow-right.svg';
import {useState} from 'react';
import {t} from 'i18next';
import AurPopup from '../../common/aur-popup/AurPopup';
import {getIndex, RebuildIndex} from '../../../api/EcaServices';
import {useNavigate} from 'react-router-dom';
import {setIndexingBannerData} from '../../../redux/slices/indexingManagementSlice';
import {formatStatus, indexingStatusKey} from '../model';
import {REBUILD_TOAST_TIMEOUT} from '../../../constants/constants';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import {AxiosError} from 'axios';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';

interface IHeaderDetails {
  title: string;
}

const ConfirmRebuildContent = () => {
  return (
    <>
      <Typography className={`typo-text body2_size content-text`}>
        {t('indexes.rebuild.content')}
      </Typography>
      <Typography
        className={`typo-text body2_size content-text rebuild-subcontent`}
      >
        {t('indexes.rebuild.subContent')}
      </Typography>
    </>
  );
};

const IndexingHeader = (props: IHeaderDetails) => {
  const {title} = props;
  const dispatch = useDispatch();
  const {showToast} = useToast();
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [rebuildLoader, setRebuildLoader] = useState<boolean>(false);

  const indexDetails = useSelector(
    (state: RootState) => state.indexes.indexData
  );

  const rebuildTrigger = useSelector(
    (state: RootState) => state.indexes.rebuildTrigger
  );

  const {clientId, projectId} = useSelector(
    (state: RootState) => state.reports
  );
  const navigate = useNavigate();
  const {trackGAEvent} = useAnalytics();

  const {hasSearchesPerm} = usePermissionsByPage(
    PAGE_NAME.INDEX_OVERVIEW_HEADER
  );

  const isSearchDisabled =
    indexDetails?.ele?.status === indexingStatusKey.notFound;

  const trackDetailButtonClick = (name: string, action: string) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name,
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action,
        details: t('gaTracking.indexDetail.indexStatus', {
          status: formatStatus(
            indexDetails?.ele?.status as Parameters<typeof formatStatus>[0]
          ),
        }),
      },
    });
  };

  const confirmRebuild = () => {
    trackDetailButtonClick(
      t('gaTracking.indexDetail.rebuildIndex'),
      t('gaTracking.indexDetail.rebuildAction', {type: indexDetails?.ele?.type})
    );
    setConfirmModal(true);
  };
  const checkInProgressStatus = (
    data: {
      type: string;
      status: string;
    }[]
  ) => {
    if (!data || !Array.isArray(data)) {
      return t('common.noData');
    }
    // Filter items with the status "Indexing Event(s) In Progress"
    const inProgressItems = data.filter(
      (item) => item.status === indexingStatusKey.inProgress
    );
    const inProgressCount = inProgressItems.length;

    // Prepare the appropriate message based on the count of "Indexing Event(s) In Progress" statuses
    let message = '';
    if (inProgressCount > 1) {
      message = t('indexes.multipleIndexBannerTitle');
    } else if (inProgressCount === 1) {
      const indexType = inProgressItems[0]?.type;

      message = indexType && `${indexType} ${t('indexes.indexBannerTitle')}`;
    }

    // Dispatch the data with the correct message and status
    dispatch(
      setIndexingBannerData({
        message,
        showIndexBanner: inProgressCount > 0,
      })
    );

    if (inProgressCount > 1) {
      return t('indexing.moreThanOneProgress');
    } else if (inProgressCount === 1) {
      return t('indexing.oneInProgress');
    } else {
      return '0';
    }
  };

  const handleContinueOptionSelected = async () => {
    try {
      setRebuildLoader(true);
      const params = `?clientId=${clientId}&projectId=${projectId}`;
      const result = await RebuildIndex(
        params,
        indexDetails?.ele.fieldTemplateId
      );
      setTimeout(async () => {
        try {
          if (result?.data?.data?.requestId) {
            const response = await getIndex(params);
            if (response?.data?.data && Array.isArray(response?.data?.data)) {
              const indexRebuildData = response?.data?.data.find(
                (data: IIndexingDetails) => data.name === indexDetails.ele.name
              );
              if (indexRebuildData) {
                const responseObject: any = {ele: indexRebuildData};
                dispatch(setIndexingData(responseObject));
              }
              checkInProgressStatus(response?.data?.data);
            }
          }
        } catch (error: unknown) {
          const e = error as AxiosError;
          showToast(e.message, 'error', undefined, 'center');
        } finally {
          setRebuildLoader(false);
          setConfirmModal(false);
        }
      }, REBUILD_TOAST_TIMEOUT);
    } catch (error: unknown) {
      const e = error as AxiosError;
      setRebuildLoader(false);
      setConfirmModal(false);
      showToast(e.message, 'error', undefined, 'center');
    }
  };

  const closeModalHandler = () => {
    setConfirmModal(false);
  };
  const navigateToSettings = () => {
    trackDetailButtonClick(
      t('gaTracking.indexDetail.viewSettings'),
      t('gaTracking.indexDetail.viewedSettingsAction', {
        type: indexDetails?.ele?.type,
      })
    );
    navigate('/indexmanagement/settings');
  };

  const navigateToSearch = () => {
    if (isSearchDisabled) {
      return;
    }
    trackDetailButtonClick(
      t('gaTracking.indexDetail.searchThisIndex'),
      t('gaTracking.indexDetail.searchedAction', {type: indexDetails?.ele?.type})
    );
    if (indexDetails.ele.type === 'Nlp') {
      navigate('/nlp-search', {state: {from: window.location.pathname}});
    } else {
      dispatch(setSelectedIndex(indexDetails.ele.name));
      navigate('/index-search', {state: {fromManageIndex: true}});
    }
  };

  return (
    <div>
      <GlowRight className="index-glow-effect-detail-page" />
      <Box className="indexing-header-view">
        <Box>
          <Typography
            component="h1"
            tabIndex={0}
            className="typo-text headline1_size mist-color-100"
          >
            {title}
          </Typography>
        </Box>
        <Box className="index-action-button">
          {indexDetails?.ele?.type !== 'Standard' && (
            <AurButton
              type={'continue'}
              label={t('indexing.rebuildIndex')}
              size={'Medium'}
              disabled={rebuildTrigger || indexDetails?.ele?.type === 'Nlp'}
              ariaLabel={t('indexing.rebuildIndex')}
              ariaDescription={
                rebuildTrigger || indexDetails?.ele?.type === 'Nlp'
                  ? 'disabled'
                  : undefined
              }
              startIcon={
                <div className="index-button-icon" aria-hidden="true">
                  {rebuildTrigger ? (
                    <AurSvgIcon src="images/rebuild.svg" />
                  ) : (
                    <AurSvgIcon src="images/rebuild_active.svg" />
                  )}
                </div>
              }
              onClick={confirmRebuild}
              disableHint={
                rebuildTrigger || indexDetails?.ele?.type === 'Nlp'
                  ? t('indexing.rebuildIndexInProgressHint')
                  : t('indexing.rebuildIndexHint')
              }
            />
          )}
          {hasSearchesPerm && (
            <AurButton
              type={'continue'}
              label={t('indexing.searchThisIndex')}
              ariaLabel={t('indexing.searchThisIndex')}
              ariaDescription={
                isSearchDisabled ? 'disabled' : t('indexing.searchThisIndexHint')
              }
              size={'Medium'}
              disabled={isSearchDisabled}
              disableHint={
                isSearchDisabled
                  ? t('indexing.searchThisIndexNotFoundHint')
                  : undefined
              }
              startIcon={
                <div className="index-button-icon" aria-hidden="true">
                  <AurSvgIcon src="images/search-icon.svg" />
                </div>
              }
              onClick={navigateToSearch}
            />
          )}
          {(indexDetails?.ele?.type === 'Standard' ||
            indexDetails?.ele?.type === 'Custom') && (
            <AurButton
              type={'continue'}
              label={t('indexing.viewSettings')}
              ariaLabel={t('indexing.viewSettings')}
              ariaDescription={t(
                'accessibility.indexManagement.detail.viewSettingHint'
              )}
              size={'Medium'}
              startIcon={
                <div
                  className="index-button-icon index-settings-icon"
                  aria-hidden="true"
                >
                  <AurIcon
                    icon={IconNames.Settings}
                    color={IconColors.Custom}
                  />
                </div>
              }
              onClick={navigateToSettings}
            />
          )}
        </Box>
      </Box>
      <IndexingDetails ecaIndexData={indexDetails?.ele} />
      {confirmModal && (
        <div>
          <AurPopup
            className="aur-unsaved-changes-popup"
            open={confirmModal}
            onOk={handleContinueOptionSelected}
            onCancel={closeModalHandler}
            popupHeading={t('common.justToConfirm')}
            showPopupHeading={true}
            showPopupTitle={true}
            popupTitle={t('common.unSavedChangesPopup.title')}
            showCloseIcon={false}
            popupContent={<ConfirmRebuildContent />}
            contentType="element"
            labelActionCancel={t('common.clearAllPopup.buttonCancel')}
            labelActionOk={t('buttons.continue')}
            exitButtonHint={t('common.clearAllPopup.cacnelToClearHint')}
            exitButtonAriaHintLabel={t(
              'accessibility.indexManagement.detail.cancelRefreshPopUpHint'
            )}
            continueButtonHint={t('common.clearAllPopup.confirmToClearHint')}
            continueButtonAriaHintLabel={t(
              'accessibility.indexManagement.detail.continueRefreshPopUpHint'
            )}
            cancelButtonTitle=""
            headingIcon={
              <AurIcon
                icon={IconNames.Error}
                color={IconColors.Primary}
                customClassName="heading-warning-icon"
              />
            }
          />
        </div>
      )}
      <AurLoadingIndicator overlay={true} isOpen={rebuildLoader} />
    </div>
  );
};

export default IndexingHeader;
